import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FaEnvelope, FaMapMarkerAlt, FaBars, FaTimes, FaUserCircle, 
  FaChevronDown, FaPhoneAlt, FaHome, FaInfoCircle, 
  FaGavel, FaImages, FaMapMarkedAlt, FaEnvelopeOpenText,
  FaSignInAlt, FaUserPlus, FaSignOutAlt, FaTachometerAlt,
  FaShieldAlt, FaSearch, FaHeart, FaBell, FaMoon, FaSun,
  FaWhatsapp, FaInstagram, FaFacebookF, FaLinkedinIn,
  FaGift
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import logo from '../assets/images/logo_new.jpeg';
import { setValue } from '../features/UserInfo/userSlice';

const Header = ({ isRefresh, setIsRefresh }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const profileRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const userData = useSelector(state => state.user_info);
  const isLoggedIn = userData && userData.length > 0;
  const isAdmin = isLoggedIn && userData[0]?.user?.email === 'prayagproperties2020@gmail.com';

  const menuItems = [
    { name: 'Home', path: '/', icon: FaHome, description: 'Welcome to our platform' },
    { name: 'About Us', path: '/about-us', icon: FaInfoCircle, description: 'Learn about our story' },
    // { name: 'Rewards', path: '/rewards', icon: FaGift, description: 'Exciting reward schemes' },
    { name: 'Legal', path: '/legal', icon: FaGavel, description: 'Terms & policies' },
    { name: 'Gallery', path: '/gallery', icon: FaImages, description: 'View our projects' },
    { name: 'Plot Availability', path: '/plot-availability', icon: FaMapMarkedAlt, description: 'Check available plots' },
    { name: 'Contact Us', path: '/contact-us', icon: FaEnvelopeOpenText, description: 'Get in touch' }
  ];

  const authItems = !isLoggedIn ? [
    { name: 'Register', path: '/register', icon: FaUserPlus, variant: 'primary' },
    { name: 'Login', path: '/login', icon: FaSignInAlt, variant: 'secondary' }
  ] : [];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    // Check saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setIsProfileOpen(false);
    setIsMenuOpen(false);
    if (setIsRefresh) {
      setIsRefresh("again");
    }
    dispatch(setValue("Logout"));
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${searchQuery}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 }
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0, transition: { duration: 0.3 } },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: { duration: 0.4, ease: "easeInOut" }
    },
    exit: { 
      opacity: 0, 
      height: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <>
      {/* Top Announcement Bar */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#021437] via-[#021437] to-[#021437] text-white font-['Inter']">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23C7C091\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-center py-2.5 gap-2 text-xs md:text-sm">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 group cursor-default">
                <div className="relative">
                  <FaMapMarkerAlt className="text-[#C7C091] group-hover:scale-110 transition-transform duration-300" />
                  <span className="absolute -inset-1 bg-[#C7C091]/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity"></span>
                </div>
                <span className="group-hover:text-[#C7C091] transition-colors">
                  Shop No. 6, Milan Plaza, Lal Bahadur Shastri Marg, Civil Lines, Prayagraj, Uttar Pradesh – 211001
                </span>
              </div>
              <div className="hidden md:block w-px h-4 bg-gradient-to-b from-transparent via-[#C7C091]/30 to-transparent"></div>
              <a href="mailto:dharaapariwar@gmail.com" className="flex items-center gap-2 group">
                <FaEnvelope className="text-[#C7C091] group-hover:scale-110 transition-transform" />
                <span className="group-hover:text-[#C7C091] transition-colors">dharaapariwar@gmail.com</span>
              </a>
              <div className="hidden md:block w-px h-4 bg-gradient-to-b from-transparent via-[#C7C091]/30 to-transparent"></div>
              <a href="tel:+919876543210" className="flex items-center gap-2 group">
                <FaPhoneAlt className="text-[#C7C091] group-hover:scale-110 transition-transform" />
                <span className="group-hover:text-[#C7C091] transition-colors">+91 63866 46807</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute h-2 w-2 rounded-full bg-[#C7C091] opacity-75"></span>
                  <span className="relative rounded-full h-2 w-2 bg-[#8F8431]"></span>
                </span>
                <span className="text-[#C7C091]">Open for inquiries</span>
              </div>
              <div className="flex gap-2">
                <a href="https://wa.me/916386646807" target='_blank' className="text-white/60 hover:text-[#C7C091] transition-colors"><FaWhatsapp size={12} /></a>
                <a href="https://www.instagram.com/dharaapariwar/" target='_blank' className="text-white/60 hover:text-[#C7C091] transition-colors"><FaInstagram size={12} /></a>
                <a href="https://www.facebook.com/profile.php?id=61593867340158" target='_blank' className="text-white/60 hover:text-[#C7C091] transition-colors"><FaFacebookF size={12} /></a>
                <a href="https://www.linkedin.com/in/dharaa-pariwar-0a1b0a21/" target='_blank' className="text-white/60 hover:text-[#C7C091] transition-colors"><FaLinkedinIn size={12} /></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <motion.header
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className={`sticky top-0 z-50 transition-all duration-500 font-['Inter'] ${
          isScrolled 
            ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl border-b border-gray-200/20 dark:border-gray-700/30' 
            : 'bg-white dark:bg-gray-900 shadow-lg'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 md:h-24">
            {/* Logo Section */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-shrink-0"
            >
              <Link to="/" className="flex items-center gap-3 group">
                <div className="relative">
                  <motion.img
                    src={logo}
                    alt="Dharaa Pariwar"
                    className={`transition-all duration-500 ${
                      isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'
                    } rounded-xl object-contain`}
                    whileHover={{ rotate: [0, -5, 5, -5, 0], transition: { duration: 0.5 } }}
                  />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#021437]/0 via-[#021437]/5 to-[#021437]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                </div>
                <div className="hidden sm:block">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-[#021437] to-[#8F8431] bg-clip-text text-transparent">
                    Dharaa Pariwar
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Your Dream Home Awaits</p>
                </div>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <motion.div
                    key={item.name}
                    variants={itemVariants}
                    className="relative"
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
                        isActive 
                          ? 'text-[#8F8431] bg-gradient-to-r from-[#8F8431]/10 to-transparent' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-100 dark:hover:from-gray-800 hover:to-transparent'
                      }`}
                    >
                      <Icon className={`text-lg transition-colors ${isActive ? 'text-[#8F8431]' : 'group-hover:text-[#8F8431]'}`} />
                      <span className="font-medium text-sm">{item.name}</span>
                    </Link>
                    {isActive && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-[#8F8431] to-[#C7C091] rounded-full"
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </motion.div>
                );
              })}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              {/* Search Button */}
              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="relative p-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              >
                <FaSearch size={18} />
              </motion.button> */}

              {/* Theme Toggle */}
              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className="relative p-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              >
                {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
              </motion.button> */}

              {/* Favorites */}
              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative p-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
              >
                <FaHeart size={18} />
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-gradient-to-r from-[#8F8431] to-[#C7C091] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                  0
                </span>
              </motion.button> */}

              {/* Auth Buttons */}
              {!isLoggedIn ? (
                <div className="hidden md:flex items-center gap-2 ml-2">
                  <Link
                    to="/register"
                    className="px-5 py-2.5 bg-gradient-to-r from-[#021437] to-[#8F8431] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-[#8F8431]/30 transition-all duration-300"
                  >
                    Get Started
                  </Link>
                  <Link
                    to="/login"
                    className="px-5 py-2.5 border-2 border-[#8F8431] text-[#8F8431] rounded-xl font-semibold text-sm hover:bg-[#8F8431] hover:text-white transition-all duration-300"
                  >
                    Sign In
                  </Link>
                </div>
              ) : (
                <div className="relative ml-2" ref={profileRef}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-[#8F8431]/10 to-transparent hover:from-[#8F8431]/20 transition-all"
                  >
                    <div className="relative">
                      <FaUserCircle className="h-8 w-8 text-[#8F8431]" />
                      <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900"></span>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden xl:inline">
                      {userData[0]?.user?.name?.split(' ')[0] || 'User'}
                    </span>
                    <FaChevronDown className={`text-xs text-gray-500 transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </motion.button>

                  <AnimatePresence>
                    {isProfileOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-3 w-72 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl py-2 z-50 border border-gray-200 dark:border-gray-700 overflow-hidden"
                      >
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-[#8F8431]/5 to-transparent">
                          <div className="flex items-center gap-3">
                            <FaUserCircle className="h-10 w-10 text-[#8F8431]" />
                            <div>
                              <p className="text-sm font-semibold text-gray-800 dark:text-white">
                                {userData[0]?.user?.name || 'User'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {userData[0]?.user?.email || 'user@example.com'}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <Link
                          to={userData[0]?.type === 'user' ? '/dashboard' : '/customer/dashboard'}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-[#8F8431]/10 hover:to-transparent transition-all group"
                        >
                          <FaTachometerAlt className="text-[#8F8431] text-base group-hover:scale-110 transition-transform" />
                          Dashboard
                        </Link>
                        
                        {isAdmin && (
                          <button
                            onClick={() => {
                              setIsProfileOpen(false);
                              navigate('/admin');
                            }}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-[#8F8431]/10 hover:to-transparent transition-all w-full group"
                          >
                            <FaShieldAlt className="text-[#8F8431] text-base group-hover:scale-110 transition-transform" />
                            Admin Panel
                          </button>
                        )}
                        
                        <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all w-full group"
                          >
                            <FaSignOutAlt className="text-base group-hover:scale-110 transition-transform" />
                            Logout
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="relative p-2.5 rounded-xl bg-gradient-to-r from-[#8F8431]/10 to-transparent hover:from-[#8F8431]/20 transition-all"
                >
                  <AnimatePresence mode="wait">
                    {isMenuOpen ? (
                      <motion.div
                        key="close"
                        initial={{ rotate: -90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: 90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <FaTimes className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ rotate: 90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: -90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <FaBars className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-full left-0 right-0 bg-white dark:bg-gray-900 shadow-2xl border-t border-gray-200 dark:border-gray-800"
            >
              <div className="max-w-3xl mx-auto px-4 py-6" ref={searchRef}>
                <form onSubmit={handleSearch} className="relative">
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for properties, locations, or projects..."
                    className="w-full pl-12 pr-24 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-transparent focus:border-[#8F8431] focus:outline-none transition-colors text-gray-800 dark:text-white"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 px-5 py-2 bg-gradient-to-r from-[#021437] to-[#8F8431] text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                  >
                    Search
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="lg:hidden overflow-hidden bg-white dark:bg-gray-900 shadow-2xl border-t border-gray-200 dark:border-gray-800"
            >
              <div className="px-4 pt-4 pb-6 space-y-1 max-h-[calc(100vh-100px)] overflow-y-auto">
                {isLoggedIn && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-4 bg-gradient-to-r from-[#8F8431]/10 to-transparent rounded-2xl border border-[#8F8431]/20"
                  >
                    <div className="flex items-center gap-3">
                      <FaUserCircle className="h-12 w-12 text-[#8F8431]" />
                      <div>
                        <p className="font-semibold text-gray-800 dark:text-white">{userData[0]?.user?.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{userData[0]?.user?.email}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <motion.div
                      key={item.name}
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all group ${
                          isActive 
                            ? 'bg-gradient-to-r from-[#8F8431]/10 to-transparent text-[#8F8431]' 
                            : 'hover:bg-gradient-to-r hover:from-gray-100 dark:hover:from-gray-800 hover:to-transparent'
                        }`}
                      >
                        <Icon className={`text-lg ${isActive ? 'text-[#8F8431]' : 'text-gray-500'} group-hover:scale-110 transition-transform`} />
                        <span className="font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                        <span className="text-xs text-gray-400 ml-auto">{item.description}</span>
                      </Link>
                    </motion.div>
                  );
                })}
                
                {!isLoggedIn ? (
                  <>
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent my-4" />
                    <div className="grid grid-cols-2 gap-3">
                      <Link
                        to="/register"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold bg-gradient-to-r from-[#021437] to-[#8F8431] text-white"
                      >
                        <FaUserPlus /> Register
                      </Link>
                      <Link
                        to="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold border-2 border-[#8F8431] text-[#8F8431]"
                      >
                        <FaSignInAlt /> Login
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent my-4" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 w-full"
                    >
                      <FaSignOutAlt /> Logout
                    </button>
                  </>
                )}
                
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 justify-center">
                    <div className="flex items-center gap-2"><FaMapMarkerAlt className="text-[#8F8431]" /> Prayagraj, UP</div>
                    <div className="flex items-center gap-2"><FaPhoneAlt className="text-[#8F8431]" /> +91 98765 43210</div>
                    <div className="flex items-center gap-2"><FaEnvelope className="text-[#8F8431]" /> info@dharaapariwar.com</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default Header;